import type { Ref } from 'react';
import { Box } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { resolveTone, type StatusSeverity } from './statusSeverity';

export interface CountBadgeProps {
  /** The number to render. */
  count: number;
  /** Tone of the pill (defaults to neutral). */
  tone?: StatusSeverity;
  ref?: Ref<HTMLSpanElement>;
}

/**
 * Small tabular-numeric count pill (the prototype kit's `CountBadge`). Used for
 * the equipment-package member counts (Devices / Plans / Approval rules). Tone
 * defaults to neutral; the fill/border follow the same alpha recipe as
 * {@link StatusChip}.
 */
export function CountBadge({ count, tone = 'neutral', ref }: CountBadgeProps) {
  const theme = useTheme();
  const resolved = resolveTone(theme, tone);
  return (
    <Box
      component="span"
      ref={ref}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: 24,
        height: 22,
        px: 0.75,
        borderRadius: 999,
        bgcolor: alpha(resolved.main, 0.1),
        border: `1px solid ${alpha(resolved.main, 0.28)}`,
        color: resolved.dark,
        fontFamily: theme.typography.button.fontFamily,
        fontSize: 12,
        fontWeight: 700,
        lineHeight: 1,
        fontVariantNumeric: 'tabular-nums',
      }}
    >
      {count}
    </Box>
  );
}
